import {
  Badge,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Tooltip,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { useState, type ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Logout as LogoutIcon,
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
} from '@mui/icons-material'
import {
  useAppDispatch,
  useAppSelector,
  toggleSidebar,
  setSidebarMobileOpen,
  toggleTheme,
} from '@/store'
import { useLogout } from '@/services/auth'
import { useBrand } from '@/branding'
import type { PermissionKey } from '@/services/permissions'
import CustomDialog from '@/components/common/custom-dialog'

const DRAWER_WIDTH = 220
const DRAWER_COLLAPSED = 60

export interface SidebarNavItem {
  to: string
  icon: ReactNode
  label: string
  end?: boolean
  /**
   * Optional count shown next to the item (e.g. open assigned tasks).
   * Hidden when undefined or 0.
   */
  badge?: number
  /** Hover text / accessible name explaining what the badge count represents. */
  badgeTooltip?: string
  /** Shown only when the user holds this permission (any one of several). Absent = always. */
  permission?: PermissionKey | PermissionKey[]
}

const BADGE_MAX = 99

interface SidebarProps {
  /** Main navigation items rendered in the scrollable middle section. */
  navItems: SidebarNavItem[]
  /** Items rendered in the footer, above Sign Out (e.g. Settings, Profile). */
  footerItems?: SidebarNavItem[]
  /** Sign-out target path (defaults to clinician login). */
  signOutRedirectTo?: string
  /**
   * Route of the Profile footer item. When the user has an avatar, that item's
   * icon is replaced with their profile photo.
   */
  profileItemTo?: string
  /** Current user's avatar (data URL, http URL, or raw base64). */
  avatarUrl?: string
}

export const Sidebar = ({
  navItems,
  footerItems = [],
  signOutRedirectTo,
  profileItemTo,
  avatarUrl,
}: SidebarProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const dispatch = useAppDispatch()
  const collapsed = useAppSelector((s) => s.ui.sidebarCollapsed)
  const mobileOpen = useAppSelector((s) => s.ui.sidebarMobileOpen)
  const themeMode = useAppSelector((s) => s.ui.themeMode)
  const navigate = useNavigate()
  const location = useLocation()
  const brand = useBrand()
  const { logout } = useLogout()
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)

  const drawerWidth = collapsed && !isMobile ? DRAWER_COLLAPSED : DRAWER_WIDTH

  const isActive = (to: string, end?: boolean) =>
    end ? location.pathname === to : location.pathname.startsWith(to)

  // Normalize the avatar source — supports data URLs, http URLs, and raw base64.
  const avatarSrc = avatarUrl
    ? avatarUrl.startsWith('data:') || avatarUrl.startsWith('http')
      ? avatarUrl
      : `data:image/jpeg;base64,${avatarUrl}`
    : undefined

  const handleNavClick = (to: string) => {
    navigate(to)
    if (isMobile) dispatch(setSidebarMobileOpen(false))
  }

  const renderNavButton = (
    label: string,
    icon: ReactNode,
    onClick: () => void,
    active = false,
    badge?: number,
    badgeTooltip?: string,
  ) => {
    const showBadge = typeof badge === 'number' && badge > 0
    const badgeLabel = badge && badge > BADGE_MAX ? `${BADGE_MAX}+` : String(badge ?? 0)
    const badgeDescription = badgeTooltip ?? `${badgeLabel} open`
    // Collapsed rail: dot-style count on the icon. Expanded: pill at the row end.
    const iconWithBadge =
      showBadge && collapsed ? (
        <Badge badgeContent={badge} max={BADGE_MAX} color="error">
          {icon}
        </Badge>
      ) : (
        icon
      )

    const button = (
      <ListItemButton
        selected={active}
        onClick={onClick}
        aria-label={showBadge ? `${label}, ${badgeDescription}` : undefined}
        sx={{ py: 0.5, px: 1.5, minHeight: 36 }}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 'auto' : 40,
            color: active ? 'primary.main' : 'text.secondary',
          }}
        >
          {iconWithBadge}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={label}
            slotProps={{ primary: { fontSize: '0.875rem', fontWeight: 500 } }}
          />
        )}
        {!collapsed && showBadge && (
          <Tooltip title={badgeDescription} placement="right" arrow>
            <Box
              component="span"
              data-testid={`nav-badge-${label}`}
              sx={{
                ml: 1,
                px: 0.75,
                minWidth: 20,
                height: 20,
                lineHeight: '20px',
                borderRadius: 10,
                fontSize: '0.75rem',
                fontWeight: 600,
                textAlign: 'center',
                color: 'error.contrastText',
                bgcolor: 'error.main',
                flexShrink: 0,
              }}
            >
              {badgeLabel}
            </Box>
          </Tooltip>
        )}
      </ListItemButton>
    )

    return collapsed ? (
      <Tooltip
        key={label}
        title={showBadge ? `${label} (${badgeDescription})` : label}
        placement="right"
        arrow
      >
        {button}
      </Tooltip>
    ) : (
      button
    )
  }

  const sidebarContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <Box
        sx={{
          height: 88,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          component="img"
          src={collapsed ? brand.favicon : brand.logo}
          alt={brand.name}
          sx={{
            height: collapsed ? 44 : 64,
            maxWidth: '100%',
            width: 'auto',
            objectFit: 'contain',
            flexShrink: 0,
            borderRadius: 1,
          }}
        />
      </Box>

      {/* Nav */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1.5, px: 1 }}>
        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {navItems.map((item) => (
            <Box key={item.to}>
              {renderNavButton(
                item.label,
                item.icon,
                () => handleNavClick(item.to),
                isActive(item.to, item.end),
                item.badge,
                item.badgeTooltip,
              )}
            </Box>
          ))}
        </List>
      </Box>

      {/* Footer */}
      <Divider />
      <Box sx={{ p: 1 }}>
        {footerItems.map((item) => {
          const isProfileItem = !!profileItemTo && item.to === profileItemTo
          const icon =
            isProfileItem && avatarSrc ? (
              <Avatar src={avatarSrc} sx={{ width: 24, height: 24 }} />
            ) : (
              item.icon
            )
          return (
            <Box key={item.to}>
              {renderNavButton(
                item.label,
                icon,
                () => handleNavClick(item.to),
                isActive(item.to, item.end),
              )}
            </Box>
          )
        })}
        {renderNavButton(
          themeMode === 'dark' ? 'Light mode' : 'Dark mode',
          themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />,
          () => dispatch(toggleTheme()),
        )}
        {renderNavButton('Sign Out', <LogoutIcon />, () => setShowLogoutConfirm(true))}
      </Box>

      {/* Collapse toggle (desktop only) */}
      {!isMobile && (
        <>
          <Divider />
          <Box
            sx={{
              display: 'flex',
              justifyContent: collapsed ? 'center' : 'flex-start',
              alignItems: 'center',
              px: collapsed ? 0 : 2.5,
              py: 1,
            }}
          >
            <Tooltip title={collapsed ? 'Expand' : 'Collapse'} placement="right" arrow>
              <IconButton size="small" onClick={() => dispatch(toggleSidebar())} sx={{ ml: -0.5 }}>
                {collapsed ? <ExpandIcon fontSize="medium" /> : <CollapseIcon fontSize="medium" />}
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}
    </Box>
  )

  const logoutDialog = (
    <CustomDialog
      openCustomDialog={showLogoutConfirm}
      setOpenCustomDialog={() => setShowLogoutConfirm(false)}
      labelHeader="Confirm Sign Out"
      label="Are you sure you want to sign out?"
      leftButtonLabel="Cancel"
      rightButtonLabel="Sign Out"
      onRightButtonClick={() => {
        setShowLogoutConfirm(false)
        logout(signOutRedirectTo)
      }}
    />
  )

  if (isMobile) {
    return (
      <>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => dispatch(setSidebarMobileOpen(false))}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
        >
          {sidebarContent}
        </Drawer>
        {logoutDialog}
      </>
    )
  }

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: 'width 0.2s',
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            transition: 'width 0.2s',
            overflowX: 'hidden',
          },
        }}
      >
        {sidebarContent}
      </Drawer>
      {logoutDialog}
    </>
  )
}
